import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el as elM, en as enM } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Section {
  key: string;
  title: string;
  items: string[];
}

const SECTIONS: { key: string; title: string; icon: string }[] = [
  { key: 'project_purpose', title: 'Σκοπός Έργου', icon: '🎯' },
  { key: 'project_scope', title: 'Αντικείμενο Έργου', icon: '📋' },
  { key: 'technical_solution', title: 'Περιγραφή Τεχνικής Λύσης', icon: '⚙️' },
  { key: 'key_risks', title: 'Κύρια Ρίσκα', icon: '⚠️' },
  { key: 'prerequisites', title: 'Βασικές Προαπαιτήσεις', icon: '✅' },
  { key: 'next_steps', title: 'Επόμενα Βήματα', icon: '➡️' },
];

const WarnIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const KickoffPresentation: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit}) => {
  const { language } = useLanguage();
  const M = language === 'en' ? enM : elM;
  const L = M.kickoff;
  const C = M.common;
  const [data, setData] = useState<Record<string, string[]> | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Record<string, string[]>>({});
  const [showAIConfirm, setShowAIConfirm] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(
        `${API}/modules/${project.id}/Kickoff%20Presentation`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.data && Object.keys(res.data.data).length > 0) {
        setData(res.data.data);
        setFormData(res.data.data);
      }
    } catch { }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    setShowAIConfirm(false);
    setGenerating(true);
    try {
      const risksRes = await axios.get(
        `${API}/modules/${project.id}/Risk%20Management`,
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => ({ data: null }));

      const risks = risksRes.data?.data?.risks || [];

      const res = await axios.post(
        `${API}/modules/${project.id}/kickoff/generate`,
        {
          aiAnalysis: project.ai_analysis || {},
          risks,
          userId: user.id,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setData(res.data);
      setFormData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/modules/${project.id}/Kickoff%20Presentation`,
        { data: formData, userId: user.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(formData);
      setEditing(false);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const addItem = (key: string) => {
    setFormData({ ...formData, [key]: [...(formData[key] || []), ''] });
  };

  const updateItem = (key: string, i: number, val: string) => {
    const updated = [...(formData[key] || [])];
    updated[i] = val;
    setFormData({ ...formData, [key]: updated });
  };

  const removeItem = (key: string, i: number) => {
    setFormData({ ...formData, [key]: (formData[key] || []).filter((_, idx) => idx !== i) });
  };

  const exportToWord = async () => {
    if (!data) return;
    const children: any[] = [
      new Paragraph({
        text: 'KICKOFF PRESENTATION',
        heading: HeadingLevel.HEADING_1,
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      }),
      new Paragraph({
        text: project.name,
        heading: HeadingLevel.HEADING_2,
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
      }),
    ];

    SECTIONS.forEach(section => {
      const items = data[section.key] || [];
      if (items.length === 0) return;

      children.push(new Paragraph({
        text: L.sections[section.key] ?? section.title,
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 400, after: 150 },
      }));

      items.forEach(item => {
        children.push(new Paragraph({
          children: [new TextRun({ text: `• ${item}`, size: 22 })],
          spacing: { after: 80 },
          indent: { left: 360 },
        }));
      });
    });

    children.push(new Paragraph({
      children: [new TextRun({
        text: C.wordFooter(user.full_name, new Date().toLocaleDateString(C.dateLocale)),
        italics: true, size: 18, color: '666666',
      })],
      alignment: AlignmentType.RIGHT,
      spacing: { before: 400 },
      border: { top: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' } },
    }));

    const doc = new Document({ sections: [{ properties: {}, children }] });
    const blob = await Packer.toBlob(doc);
    saveAs(blob, `Kickoff_${project.name}.docx`);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      {/* Header */}
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      {/* Breadcrumb */}
      <div style={{ padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky', top: 64, zIndex: 99 }}>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: '500' }} onClick={onBack}>{C.myProjects}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: '500' }} onClick={onBack}>{project.name}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#374151', fontWeight: '500' }}>Kickoff Presentation</span>
      </div>

      <div style={{ padding: '32px 48px', width: '100%', boxSizing: 'border-box' }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' }}>Kickoff Presentation</h2>
            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{L.subtitle}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {data && !editing && (
              <button onClick={exportToWord} style={{ backgroundColor: '#2b579a', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                {C.exportWord}
              </button>
            )}
            {canEdit && (
              editing ? (
                <>
                  <button onClick={() => { setEditing(false); setFormData(data || {}); }} style={{ backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>{C.cancel}</button>
                  <button onClick={handleSave} disabled={saving} style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                    {saving ? C.saving : C.save}
                  </button>
                </>
              ) : (
                <>
                  {data ? (
                    <button onClick={() => setEditing(true)} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                      {C.edit}
                    </button>
                  ) : (
                    <button onClick={() => setShowAIConfirm(true)} disabled={generating} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                      {generating ? C.generating : C.createAI}
                    </button>
                  )}
                </>
              )
            )}
          </div>
        </div>

        {loading ? (
          <p style={{ color: '#666' }}>{C.loading}</p>
        ) : generating ? (
          <div style={{ backgroundColor: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>✦</div>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#4f46e5', margin: '0 0 6px' }}>{L.generatingTitle}</p>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>{L.analyzingContract}</p>
          </div>
        ) : !data ? (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '60px', textAlign: 'center', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>🚀</div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px' }}>{L.emptyTitle}</h3>
            <p style={{ fontSize: '14px', color: '#9ca3af', margin: '0 0 24px' }}>{L.emptyDesc}</p>
            {canEdit && (
              <button onClick={() => setShowAIConfirm(true)} disabled={generating} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '12px 28px', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
                {C.createAI}
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {SECTIONS.map(section => {
              const items = editing ? (formData[section.key] || []) : (data[section.key] || []);
              return (
                <div key={section.key} style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', borderLeft: '4px solid #4f46e5' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{section.icon}</span>
                    {L.sections[section.key] ?? section.title}
                  </h3>

                  {editing && canEdit ? (
                    <div>
                      {(formData[section.key] || []).map((item, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                          <span style={{ color: '#4f46e5', fontWeight: '600', fontSize: '14px', flexShrink: 0 }}>•</span>
                          <input
                            style={{ flex: 1, padding: '7px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', fontFamily: 'inherit', outline: 'none' }}
                            value={item}
                            onChange={e => updateItem(section.key, i, e.target.value)}
                            placeholder="Bullet point..."
                          />
                          <button onClick={() => removeItem(section.key, i)} style={{ backgroundColor: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', flexShrink: 0 }}>✕</button>
                        </div>
                      ))}
                      <button onClick={() => addItem(section.key)} style={{ backgroundColor: 'transparent', border: '1px dashed #4f46e5', color: '#4f46e5', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', marginTop: '4px' }}>
                        {L.addBullet}
                      </button>
                    </div>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: '0', listStyle: 'none' }}>
                      {items.length === 0 ? (
                        <li style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>{L.noData}</li>
                      ) : items.map((item, i) => (
                        <li key={i} style={{ fontSize: '14px', color: '#374151', marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px', lineHeight: '1.5' }}>
                          <span style={{ color: '#4f46e5', fontWeight: '700', flexShrink: 0, marginTop: '1px' }}>•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAIConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '28px 32px', width: '420px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7c3aed', flexShrink: 0, fontSize: '20px' }}>✦</div>
              <div>
                <p style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' }}>{L.aiConfirmTitle}</p>
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{C.aiConfirmSubtitle}</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', backgroundColor: '#fffbeb', borderRadius: '8px', padding: '10px 12px', marginBottom: '20px', border: '1px solid #fde68a', color: '#92400e' }}>
              <WarnIcon />
              <p style={{ fontSize: '13px', margin: 0, lineHeight: 1.5, color: '#92400e' }}>{L.aiConfirmWarning}</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowAIConfirm(false)} style={{ backgroundColor: 'transparent', border: '1px solid #ddd', color: '#374151', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>{C.back}</button>
              <button onClick={handleGenerate} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>{C.continue}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KickoffPresentation;
